import { useCallback, useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/core";
import { BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import {
  decideTextFieldSync,
  type WaveBlock,
  type WaveDocEditor,
} from "@vida/wave-core";
import { applyAssistantDirectiveOnBlur } from "../../services/waveAssistant";
import type { DraftTextFieldEditHandlers } from "./DraftTextField";
import {
  getMarkdownFromEditor,
  waveMarkdownExtensions,
} from "./waveMarkdownExtensions";

export type WaveMarkdownFieldProps = {
  waveId: string;
  block: WaveBlock;
  editor?: WaveDocEditor | null;
  remoteContentRevision?: number;
  hasRemoteEditors?: boolean;
  readOnly?: boolean;
  /** Sanitized view for agent widgets; raw `block.payload.text` stays in CRDT. */
  displayText?: string;
  placeholder?: string;
  onUpdate?: (block: WaveBlock) => void;
  onSelectionChange?: (offset: number, endOffset: number) => void;
  edit: DraftTextFieldEditHandlers;
};

function WaveMarkdownBubbleMenu({ editor }: { editor: Editor }) {
  const run = (fn: () => boolean) => () => {
    fn();
  };

  const setLink = () => {
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL del link", previous ?? "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 120, placement: "top" }}
      className="wave-md-bubble"
    >
      <div className="wave-md-bubble-inner" role="toolbar" aria-label="Formattazione testo">
        <button type="button" className={editor.isActive("bold") ? "active" : ""} onClick={run(() => editor.chain().focus().toggleBold().run())} title="Grassetto">
          <strong>B</strong>
        </button>
        <button type="button" className={editor.isActive("italic") ? "active" : ""} onClick={run(() => editor.chain().focus().toggleItalic().run())} title="Corsivo">
          <em>I</em>
        </button>
        <button type="button" className={editor.isActive("strike") ? "active" : ""} onClick={run(() => editor.chain().focus().toggleStrike().run())} title="Barrato">
          <s>S</s>
        </button>
        <button type="button" className={editor.isActive("code") ? "active" : ""} onClick={run(() => editor.chain().focus().toggleCode().run())} title="Codice inline">
          {"</>"}
        </button>
        <span className="wave-md-bubble-sep" aria-hidden />
        <button type="button" className={editor.isActive("heading", { level: 1 }) ? "active" : ""} onClick={run(() => editor.chain().focus().toggleHeading({ level: 1 }).run())} title="Titolo 1">
          H1
        </button>
        <button type="button" className={editor.isActive("heading", { level: 2 }) ? "active" : ""} onClick={run(() => editor.chain().focus().toggleHeading({ level: 2 }).run())} title="Titolo 2">
          H2
        </button>
        <button type="button" className={editor.isActive("heading", { level: 3 }) ? "active" : ""} onClick={run(() => editor.chain().focus().toggleHeading({ level: 3 }).run())} title="Titolo 3">
          H3
        </button>
        <span className="wave-md-bubble-sep" aria-hidden />
        <button type="button" className={editor.isActive("bulletList") ? "active" : ""} onClick={run(() => editor.chain().focus().toggleBulletList().run())} title="Elenco puntato">
          •
        </button>
        <button type="button" className={editor.isActive("orderedList") ? "active" : ""} onClick={run(() => editor.chain().focus().toggleOrderedList().run())} title="Elenco numerato">
          1.
        </button>
        <button type="button" className={editor.isActive("blockquote") ? "active" : ""} onClick={run(() => editor.chain().focus().toggleBlockquote().run())} title="Citazione">
          “
        </button>
        <button type="button" onClick={setLink} className={editor.isActive("link") ? "active" : ""} title="Link">
          🔗
        </button>
        <button type="button" onClick={run(() => editor.chain().focus().toggleCodeBlock().run())} title="Blocco codice">
          {"{ }"}
        </button>
        <button
          type="button"
          onClick={run(() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
          )}
          title="Tabella"
        >
          ⊞
        </button>
        <button type="button" onClick={run(() => editor.chain().focus().setHorizontalRule().run())} title="Separatore">
          —
        </button>
      </div>
    </BubbleMenu>
  );
}

export function WaveMarkdownField({
  waveId,
  block,
  editor,
  remoteContentRevision = 0,
  hasRemoteEditors = false,
  readOnly,
  displayText,
  placeholder = "Testo",
  onUpdate,
  onSelectionChange,
  edit,
}: WaveMarkdownFieldProps) {
  const external = block.payload.text ?? "";
  const visibleText = displayText != null ? displayText : external;
  const effectiveReadOnly = readOnly || block.payload.archived === true;
  const [draft, setDraft] = useState(visibleText);
  const [isFocused, setIsFocused] = useState(false);
  const editingRef = useRef(false);
  const draftRef = useRef(external);
  const lastRemoteRevisionRef = useRef(remoteContentRevision);
  const blockIdRef = useRef(block.id);

  draftRef.current = draft;

  const commit = useCallback(
    (value: string) => {
      if (editor) {
        editor.setText(block.id, "text", value);
        return;
      }
      onUpdate?.({
        ...block,
        payload: { ...block.payload, text: value },
      });
    },
    [block, editor, onUpdate],
  );

  const tiptapEditor = useEditor(
    {
      extensions: waveMarkdownExtensions(),
      content: visibleText || "",
      editable: !effectiveReadOnly,
      editorProps: {
        attributes: {
          class: "wave-md-editor__content",
          "data-placeholder": placeholder,
        },
      },
      onUpdate: ({ editor: ed }) => {
        editingRef.current = true;
        edit.onTypingActivity?.();
        const md = getMarkdownFromEditor(ed);
        setDraft(md);
        commit(md);
        window.setTimeout(() => {
          editingRef.current = false;
        }, 50);
      },
      onFocus: () => {
        setIsFocused(true);
        edit.onFocus?.();
      },
      onBlur: ({ editor: ed }) => {
        setIsFocused(false);
        edit.onBlur?.();
        if (effectiveReadOnly) return;
        const md = getMarkdownFromEditor(ed);
        void applyAssistantDirectiveOnBlur(waveId, md, (integrated) => {
          setDraft(integrated);
          commit(integrated);
          if (integrated !== md) {
            ed.commands.setContent(integrated);
          }
        });
      },
      onSelectionUpdate: ({ editor: ed }) => {
        const { from, to } = ed.state.selection;
        onSelectionChange?.(from, to);
      },
    },
    [block.id, effectiveReadOnly, waveId],
  );

  useEffect(() => {
    if (block.id !== blockIdRef.current) {
      blockIdRef.current = block.id;
      lastRemoteRevisionRef.current = remoteContentRevision;
      draftRef.current = external;
      setDraft(external);
      if (tiptapEditor) {
        tiptapEditor.commands.setContent(external || "", false);
      }
      return;
    }

    const remoteRevisionBumped =
      remoteContentRevision !== lastRemoteRevisionRef.current;
    lastRemoteRevisionRef.current = remoteContentRevision;

    const remoteSource = displayText != null ? visibleText : external;
    const { nextDraft, shouldSync } = decideTextFieldSync({
      external: remoteSource,
      draft: draftRef.current,
      isEditing: editingRef.current,
      isFocused,
      allowRemoteOverwrite: hasRemoteEditors,
      remoteRevisionBumped,
    });

    if (!shouldSync || nextDraft === draftRef.current) return;

    draftRef.current = nextDraft;
    setDraft(nextDraft);
    if (tiptapEditor && !isFocused && !editingRef.current) {
      tiptapEditor.commands.setContent(nextDraft || "", false);
    }
  }, [
    block.id,
    external,
    visibleText,
    displayText,
    remoteContentRevision,
    hasRemoteEditors,
    isFocused,
    tiptapEditor,
  ]);

  useEffect(() => {
    if (!tiptapEditor || isFocused || editingRef.current) return;
    const currentMd = getMarkdownFromEditor(tiptapEditor);
    if (currentMd === draftRef.current) return;
    tiptapEditor.commands.setContent(draftRef.current || "", false);
  }, [tiptapEditor, draft, isFocused]);

  useEffect(() => {
    tiptapEditor?.setEditable(!effectiveReadOnly);
  }, [effectiveReadOnly, tiptapEditor]);

  return (
    <div
      className={`wave-md-editor wave-text${effectiveReadOnly ? " wave-md-editor--readonly" : ""}${isFocused ? " wave-md-editor--focused" : ""}`}
    >
      {tiptapEditor && !effectiveReadOnly && (
        <WaveMarkdownBubbleMenu editor={tiptapEditor} />
      )}
      <EditorContent editor={tiptapEditor} />
    </div>
  );
}
